import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@material-ui/core';
import { Edit, ArrowUpward, FilterList, Delete } from '@material-ui/icons';
import { destroyVenue, getVenuesWithPagination } from 'services/venueServices';
import AppContext from 'contexts/AppContext';
import EditVenueContext from 'contexts/EditVenueContext';
import ConfirmDialog from 'components/ConfirmDialog';
import DropDown from 'components/DropDown';
import type PaginationData from 'types/models/PaginationData';
import type Venue from 'types/models/Venue';
import type { VenueFilterParams } from 'types/models/filters';
import 'styles/pages/ListVenuesPage.scss';

export default (): JSX.Element => {
  const history = useHistory();
  const { isLoading, setIsLoading } = React.useContext(AppContext);
  const { setSelectedVenue, selectedVenue } = React.useContext(EditVenueContext);
  const [withFilter, setWithFilter] = React.useState<boolean>(false);
  const [venuePagination, setVenuePagination] = React.useState<PaginationData<Venue> | null>();
  const [filteredVenues, setFilteredVenues] = React.useState<Venue[]>([]);
  const [venueFilterParams, setVenueFilterParams] = React.useState<VenueFilterParams | null>({
    name: '',
    code: '',
  });
  const [sortedField, setSortedField] = React.useState<{ field: string; sort: 'asc' | 'desc' }>({
    field: '',
    sort: 'asc',
  });
  const [showFilterDropDown, setShowFilterDropDown] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const { t } = useTranslation();

  const menuItems = [
    { label: 'Destino', value: 'destination_address' },
    { label: 'Origen', value: 'origin_address' },
    { label: 'Nombre', value: 'description' },
  ];

  const searchButton = React.useRef<HTMLButtonElement>(null);

  const loadData = React.useCallback(
    async (page?: number, venueFilterParams?: VenueFilterParams | null, withFilter = false) => {
      setIsLoading(true);
      const paginationData = await getVenuesWithPagination(page, venueFilterParams, withFilter);

      if (paginationData) {
        setVenuePagination(paginationData);
        setFilteredVenues(paginationData.data);
      }

      setIsLoading(false);
    },
    [],
  );

  React.useEffect(() => {
    loadData(1, venueFilterParams, withFilter);
  }, []);

  const handleTableCellClick = async (field: string) => {
    const sort = sortedField.sort === 'asc' ? 'desc' : 'asc';
    setSortedField({ field, sort });
    const filters: VenueFilterParams = { ...venueFilterParams, sortBy: field, sortDir: sort };
    setVenueFilterParams(filters);
    setWithFilter(true);
    loadData(1, filters, withFilter);
  };

  const renderEditButton = (venue: Venue | null): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (isLoading) return;
          if (!venue) return;
          setSelectedVenue(venue);
          history.push(`/private/venues/${venue.id}/edit`);
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderDeleteButton = (data: any): JSX.Element => (
    <IconButton
      color="inherit"
      onClick={() => {
        if (isLoading) return;
        if (!data) return;
        setSelectedVenue(data);
        setOpenDialog(true);
      }}
    >
      <Delete />
    </IconButton>
  );

  const onCancel = (): void => {
    setOpenDialog(false);
  };

  const onConfirm = async (): Promise<void> => {
    await destroyVenue(selectedVenue?.id ?? '');
    loadData(1, venueFilterParams, withFilter);
  };

  const renderContent = (paginationData: Venue[] = []): JSX.Element => {
    return (
      <>
        {paginationData.map((venue: Venue) => (
          <TableRow key={venue.id}>
            <TableCell component="th" scope="row">
              {venue?.name ?? ''}
            </TableCell>
            <TableCell component="th" scope="row">
              {venue.code}
            </TableCell>

            <TableCell>
              <div className="Action--buttons">
                {renderEditButton(venue)}
                {renderDeleteButton(venue)}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <section className="Table--header">
        <h3>{t('all-venues')}</h3>
      </section>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <section className="Table--hero">
        <div className="Table--wrapper">
          <div className="Filter--wrapper">
            <Button
              size="small"
              variant="contained"
              color="primary"
              className="Filter--button"
              onClick={() => setShowFilterDropDown(!showFilterDropDown)}
            >
              <span className="noabbr">
                <FilterList />
                <p>{t('filter')}</p>
              </span>
              <span className="abbr">
                <FilterList />
              </span>
            </Button>

            <DropDown
              show={showFilterDropDown}
              onPress={() => {
                const filters: VenueFilterParams = { ...venueFilterParams, sortBy: '', sortDir: undefined };
                setWithFilter(true);
                loadData(1, filters, true);
                setShowFilterDropDown(false);
              }}
            >
              <>
                <section className="Filter--section">
                  <p>{t('search-by-column')}</p>
                  <FormControl fullWidth>
                    <Select
                      value={venueFilterParams?.searchBy}
                      labelId="categories"
                      id="categorySelect"
                      variant="outlined"
                      className="Select--filter"
                      onChange={(event) => {
                        let searchBy = '';
                        if (event && event.target && event.target.value) {
                          searchBy = event.target.value as unknown as string;
                        }
                        setVenueFilterParams({ ...venueFilterParams, searchBy });
                      }}
                      displayEmpty
                      defaultValue=""
                      disabled={isLoading}
                    >
                      <MenuItem value="">{t('all-categories')}</MenuItem>
                      {menuItems.map((item, index) => (
                        <MenuItem key={index} value={`${item.value}`}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </section>
                <section className="Filter--section">
                  <p>{t('search')}...</p>
                  <input
                    value={venueFilterParams?.search}
                    onKeyPress={(evt) => {
                      if (evt.code === 'Enter') {
                        searchButton.current?.click();
                      }
                      return;
                    }}
                    type="text"
                    onChange={(event) => {
                      setVenueFilterParams({ ...venueFilterParams, search: event.target.value.toLocaleLowerCase() });
                    }}
                    className="Searchbar--input"
                    placeholder={'Buscar…'}
                    disabled={isLoading}
                  />
                </section>
              </>
            </DropDown>
          </div>

          <Button
            size="medium"
            variant="contained"
            color="primary"
            className="NewVenue--button"
            onClick={() => history.push(`/private/venues/create`)}
          >
            <span className="noabbr">{t('new-venue')}</span>
            <span className="abbr">&#43;</span>
          </Button>
        </div>
        <TableContainer component={Paper}>
          <Table className="Mui--table">
            <TableHead>
              <TableRow>
                <TableCell className="Mui--tablecell">
                  <div className="Table--cell nowrap">
                    <p>{t('code')}</p>
                  </div>
                </TableCell>

                <TableCell className="Mui--tablecell" onClick={() => handleTableCellClick('origin_address')}>
                  <div className="Table--cell">
                    <p>{t('name')}</p>
                    <ArrowUpward
                      className={`Arrow--icon ${
                        sortedField.field === 'origin_address' ? `sortedByOrigin ${sortedField.sort}` : ''
                      }`}
                    />
                  </div>
                </TableCell>
                <TableCell>{t('components.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {venuePagination?.data ? (
                renderContent(filteredVenues)
              ) : (
                <TableRow>
                  <TableCell colSpan={7} component="th" scope="row">
                    {t('no-venues')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {venuePagination && (
          <TablePagination
            component="div"
            count={venuePagination.total}
            page={venuePagination.currentPage - 1}
            onPageChange={(_, page: number) => {
              loadData(page + 1, venueFilterParams, withFilter);
            }}
            rowsPerPage={venuePagination.perPage}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) => {
              return `${from} - ${to} de ${count !== -1 ? count : '0'}`;
            }}
          />
        )}
      </section>
    </>
  );
};
